import { findOnetrustCookiePerformance } from '@/app_lib/services/oneTrust/cookies';
import {
  classifyUser,
  UNKNOWN_USER_CLASSIFICATION,
  type UserClassification,
} from '@/app_lib/services/userClassification';
import { isIdentityClassificationEnabled } from '@/app_lib/services/userClassification/featureFlag';
import type {
  DataLayerEventName,
  DataLayerObject,
  DataLayerPageStatus,
  EventData,
  OptimizelyDecisionTracking,
  PageViewDataLayerObject,
  PushableObject,
  UserConversionObject,
  UserStateChangeObject,
} from '@/app_lib/types/Gtm';

const LOGGED_OUT_STATE = 'LoggedOut' as const;
const USER_CONVERSION_SESSION_STARTED_KNOWN_KEY = 'fortune.userConversion.sessionStartedKnown';
const USER_CONVERSION_TRACKED_KEY = 'fortune.userConversion.tracked';

let lastTrackedClassification: UserClassification = UNKNOWN_USER_CLASSIFICATION;
let lastUserStateChangeSignature: string | null = null;

const getStateLabel = (classification: UserClassification) => classification.user_state ?? LOGGED_OUT_STATE;

const buildUserStateChangeEvent = (
  previousClassification: UserClassification,
  nextClassification: UserClassification,
): UserStateChangeObject => {
  const hasPerformanceConsent = findOnetrustCookiePerformance();

  return {
    event: 'user_state_change',
    // biome-ignore lint/style/useNamingConvention: external data
    new_state: getStateLabel(nextClassification),
    // biome-ignore lint/style/useNamingConvention: external data
    piano_user_id: hasPerformanceConsent && nextClassification.user_known ? nextClassification.piano_id : null,
    // biome-ignore lint/style/useNamingConvention: external data
    previous_state: getStateLabel(previousClassification),
    // biome-ignore lint/style/useNamingConvention: external data
    user_known: nextClassification.user_known,
    // biome-ignore lint/style/useNamingConvention: external data
    user_type: nextClassification.user_type,
  };
};

const getSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage;
};

const initializeUserConversionSession = (classification: UserClassification) => {
  const sessionStorage = getSessionStorage();

  if (!sessionStorage || sessionStorage.getItem(USER_CONVERSION_SESSION_STARTED_KNOWN_KEY) !== null) {
    return;
  }

  sessionStorage.setItem(USER_CONVERSION_SESSION_STARTED_KNOWN_KEY, String(classification.user_known));
};

const hasTrackedUserConversion = () => getSessionStorage()?.getItem(USER_CONVERSION_TRACKED_KEY) === 'true';

const markUserConversionTracked = () => {
  getSessionStorage()?.setItem(USER_CONVERSION_TRACKED_KEY, 'true');
};

const sessionStartedKnown = () => getSessionStorage()?.getItem(USER_CONVERSION_SESSION_STARTED_KNOWN_KEY) === 'true';

const buildUserConversionEvent = (
  nextClassification: UserClassification,
  conversionType: 'login' | 'registration',
): UserConversionObject | null => {
  if (!(nextClassification.piano_id && nextClassification.user_type)) {
    return null;
  }

  return {
    // biome-ignore lint/style/useNamingConvention: external data
    conversion_type: conversionType,
    event: 'user_conversion',
    // biome-ignore lint/style/useNamingConvention: external data
    piano_user_id: nextClassification.piano_id,
    // biome-ignore lint/style/useNamingConvention: external data
    user_type: nextClassification.user_type,
  };
};

export interface TrackEventParams {
  data: EventData;
  eventType?: DataLayerEventName;
}

export const pushToDataLayer = (obj: PushableObject) => {
  window.dataLayer?.push(obj);
};

export const getCurrentUserClassification = (): UserClassification => classifyUser();

export const getTrackedUserClassification = (): UserClassification => lastTrackedClassification;

export const syncTrackedUserClassification = (
  classification: UserClassification = getCurrentUserClassification(),
): UserClassification => {
  initializeUserConversionSession(classification);
  lastTrackedClassification = classification;
  return classification;
};

export const trackUserConversion = ({
  conversionType,
  nextClassification = getCurrentUserClassification(),
  previousClassification = lastTrackedClassification,
}: {
  conversionType: 'login' | 'registration';
  nextClassification?: UserClassification;
  previousClassification?: UserClassification;
}): boolean => {
  initializeUserConversionSession(previousClassification);
  lastTrackedClassification = nextClassification;

  if (!isIdentityClassificationEnabled()) {
    return false;
  }

  if (sessionStartedKnown() || hasTrackedUserConversion()) {
    return false;
  }

  if (previousClassification.user_known || !nextClassification.user_known) {
    return false;
  }

  const event = buildUserConversionEvent(nextClassification, conversionType);

  if (!event) {
    return false;
  }

  markUserConversionTracked();
  pushToDataLayer(event);
  return true;
};

export const trackCurrentUserConversion = (
  conversionType: 'login' | 'registration',
  previousClassification?: UserClassification,
): boolean =>
  trackUserConversion({
    conversionType,
    nextClassification: getCurrentUserClassification(),
    previousClassification,
  });

export const trackUserStateChange = ({
  nextClassification = getCurrentUserClassification(),
  previousClassification = lastTrackedClassification,
}: {
  nextClassification?: UserClassification;
  previousClassification?: UserClassification;
} = {}): boolean => {
  lastTrackedClassification = nextClassification;

  if (!isIdentityClassificationEnabled()) {
    return false;
  }

  if (getStateLabel(previousClassification) === getStateLabel(nextClassification)) {
    return false;
  }

  const event = buildUserStateChangeEvent(previousClassification, nextClassification);
  const signature = JSON.stringify(event);

  if (signature === lastUserStateChangeSignature) {
    return false;
  }

  lastUserStateChangeSignature = signature;
  pushToDataLayer(event);
  return true;
};

export const trackCurrentUserStateChange = (previousClassification?: UserClassification): boolean =>
  trackUserStateChange({
    nextClassification: getCurrentUserClassification(),
    previousClassification,
  });

export const trackEvent = (data: EventData, eventType: DataLayerEventName = 'eventTracker') => {
  const trackingData: DataLayerObject = {
    event: eventType,
    ...data,
    nonInteraction: 0,
  };

  Object.keys(data).forEach((key) => {
    if (
      [
        'eventCategory',
        'contentPlacementCD',
        'CompanyCD',
        'rankingTitleCD',
        'userIDCD',
        'eventAction',
        'eventLabel',
      ].includes(key) &&
      data[key]
    ) {
      trackingData[key] = data[key].toLowerCase();
    }
  });

  const pageViewData = window.dataLayer?.find(
    (item) => (item as PageViewDataLayerObject).event === 'fortune.historyChange',
  );
  const optimizelyData =
    window.dataLayer?.filter((item) => (item as OptimizelyDecisionTracking).event === 'optimizely-decision') || [];
  const optimizelyVariationNames = optimizelyData
    .map((item) => (item as OptimizelyDecisionTracking).variationKey)
    .filter((name) => name !== undefined)
    .join(',');

  const cmsCategoryCd = (pageViewData as PageViewDataLayerObject)?.cmsCategoryCD?.toLowerCase() || '';

  pushToDataLayer({
    ...trackingData,
    // biome-ignore lint/style/useNamingConvention: external data
    cmsCategoryCD: cmsCategoryCd,
    optimizely: [...optimizelyData],
    optimizelyVariationNames,
  });
};

export const GtmPageView = (data: PageViewDataLayerObject) => {
  const identityEnabled = isIdentityClassificationEnabled();
  const isStillRelevant = () => !data.path || window.location?.pathname === data.path;

  const pushPageView = () => {
    if (!isStillRelevant()) {
      return;
    }

    const optimizelyData =
      window.dataLayer?.filter((item) => (item as OptimizelyDecisionTracking).event === 'optimizely-decision') || [];
    const optimizelyVariationNames = optimizelyData
      .map((item) => (item as OptimizelyDecisionTracking).variationKey)
      .filter((name) => name !== undefined)
      .join(',');

    if (identityEnabled) {
      const classification = classifyUser();
      const hasPerformanceConsent = findOnetrustCookiePerformance();

      pushToDataLayer({
        ...data,
        event: 'fortune.historyChange',
        optimizely: [...optimizelyData],
        optimizelyVariationNames,
        // biome-ignore lint/style/useNamingConvention: external data
        piano_user_id: hasPerformanceConsent && classification.user_known ? classification.piano_id : null,
        // biome-ignore lint/style/useNamingConvention: external data
        user_known: classification.user_known,
        // biome-ignore lint/style/useNamingConvention: external data
        user_state: classification.user_state,
        // biome-ignore lint/style/useNamingConvention: external data
        user_type: classification.user_type,
      });
      return;
    }

    pushToDataLayer({
      ...data,
      event: 'fortune.historyChange',
      optimizely: [...optimizelyData],
      optimizelyVariationNames,
    });
  };

  pushPageView();
};

export const getGtmPageStatusEvent = (
  isError: boolean,
  errorCode?: number,
  errorDetail?: string,
): DataLayerPageStatus => ({
  errorCode: errorCode ?? 0,
  errorDetail: errorDetail ?? '',
  event: `Page Status - ${isError ? `Error (${errorCode})` : 'Init'}`,
  isError,
});
